import {useContext, useMemo} from 'react'
import {Polyline} from 'react-leaflet'
import {MapContext} from '../context/MapContext'
import {simulatedPosition} from '../data/current-location'
import {RoutesData} from '../data/routes-data'

const toRadians = value => value * Math.PI / 180

const getDistance = ([latitudeA, longitudeA], [latitudeB, longitudeB]) => {
  const latitudeDifference = toRadians(latitudeB - latitudeA)
  const longitudeDifference = toRadians(longitudeB - longitudeA)
  const calculation =
    Math.sin(latitudeDifference / 2) ** 2 +
    Math.cos(toRadians(latitudeA)) * Math.cos(toRadians(latitudeB)) *
    Math.sin(longitudeDifference / 2) ** 2

  return 6371000 * 2 * Math.atan2(Math.sqrt(calculation), Math.sqrt(1 - calculation))
}

const toKey = ([latitude, longitude]) => `${latitude.toFixed(5)}:${longitude.toFixed(5)}`

const routeGraph = RoutesData.reduce((graph, route) => {
  route.forEach((point, index) => {
    const key = toKey(point)
    if (!graph.has(key)) graph.set(key, {point, neighbors: new Map()})

    if (index === 0) return

    const previousPoint = route[index - 1]
    const previousKey = toKey(previousPoint)
    const distance = getDistance(previousPoint, point)
    graph.get(key).neighbors.set(previousKey, distance)
    graph.get(previousKey).neighbors.set(key, distance)
  })

  return graph
}, new Map())

const getClosestNodeKey = point => [...routeGraph.entries()]
  .reduce((closest, [key, node]) => (
    getDistance(point, node.point) < closest.distance
      ? {key, distance: getDistance(point, node.point)}
      : closest
  ), {key: null, distance: Infinity}).key

const getPathBetweenNodes = (startKey, endKey) => {
  const distances = new Map([[startKey, 0]])
  const previous = new Map()
  const pending = new Set(routeGraph.keys())

  while (pending.size) {
    let currentKey = null
    let currentDistance = Infinity

    pending.forEach(key => {
      const distance = distances.get(key) ?? Infinity
      if (distance < currentDistance) {
        currentKey = key
        currentDistance = distance
      }
    })

    if (currentKey === null || currentKey === endKey) break
    pending.delete(currentKey)

    routeGraph.get(currentKey).neighbors.forEach((edgeDistance, neighborKey) => {
      if (!pending.has(neighborKey)) return
      const nextDistance = currentDistance + edgeDistance
      if (nextDistance < (distances.get(neighborKey) ?? Infinity)) {
        distances.set(neighborKey, nextDistance)
        previous.set(neighborKey, currentKey)
      }
    })
  }

  if (startKey !== endKey && !previous.has(endKey)) return []

  const path = []
  let currentKey = endKey
  while (currentKey) {
    path.unshift(routeGraph.get(currentKey).point)
    if (currentKey === startKey) break
    currentKey = previous.get(currentKey)
  }

  return path
}

const NavigationRoute = () => {
  const {routeTarget, isRouteReady} = useContext(MapContext)
  const route = useMemo(() => {
    if (!routeTarget) return []

    const target = routeTarget
    const path = getPathBetweenNodes(
      getClosestNodeKey(simulatedPosition),
      getClosestNodeKey(target)
    )

    return path.length ? [simulatedPosition, ...path, target] : [simulatedPosition, target]
  }, [routeTarget])

  if (!isRouteReady || !route.length) return null

  const routeKey = routeTarget.join('-')

  return <>
    <Polyline key={`route-outline-${routeKey}`} positions={route} pathOptions={{className: 'navigation-route navigation-route-outline', color: 'white', weight: 8, opacity: 0.9, lineCap: 'round', lineJoin: 'round'}}/>
    <Polyline key={`route-line-${routeKey}`} positions={route} pathOptions={{className: 'navigation-route navigation-route-line', color: '#2563EB', weight: 4, opacity: 0.92, lineCap: 'round', lineJoin: 'round'}}/>
  </>
}

export default NavigationRoute
